import React, { useContext, useEffect, useState } from "react";
import { Box } from "@mui/material";
import StepBox from "../../CustomComponents/Tutorial/StepBox";
import GuidedPopover from "../../CustomComponents/Tutorial/GuidedPopover";
import AppContext from "../../Contexts/AppContext";
import { useTranslation } from "react-i18next";

export default function Tutorial() {
  const { t } = useTranslation("main");

  const {
    menuRef,
    userDirectionRef,
    mapControlsRef,
    attributionsRef,
    openDrawer,
    setOpenDrawer,
    myProjectsRef,
    tabsRef,
    layersRef,
    project,
    setCarbonResponse,
    setShowTutorialCarbonWindow,
    setActualGroundUse,
    repeatTutorial,
    carbonCategoryRef,
    avatarRef,
    setAnchorElAvatar,
    avatarTutorialButtonRef,
  } = useContext(AppContext);

  const [currentStep, setCurrentStep] = useState(0);
  const [changingStep, setChangingStep] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showBasicTutorial, setShowBasicTutorial] = useState(false);
  const [showCarbonTutorial, setShowCarbonTutorial] = useState(false);

  const isMobile = window.innerWidth < 960;

  const handleNextStep = () => {
    setChangingStep(false);
    setCurrentStep((prev) => prev + 1);
  };
  const handlePreviousStep = () => {
    setChangingStep(false);
    setCurrentStep((prev) => prev - 1);
  };
  const handleSkip = () => {
    if (showCarbonTutorial) {
      setCarbonResponse({});
      setActualGroundUse({});
      setShowTutorialCarbonWindow(false);
    }
    if (showBasicTutorial) {
      setOpenDrawer(false);
    }
    setShowTutorial(false);
    localStorage.setItem("watched_basic_tutorial", "true");
    localStorage.setItem("watched_carbon_tutorial", "true");
  };
  const handleFinishTutorial = () => {
    setAnchorElAvatar();

    setShowTutorial(false);
  };

  useEffect(() => {
    if (!project) return;

    const tutorials = {
      watched_basic_tutorial: { setter: setShowBasicTutorial },
      watched_carbon_tutorial: {
        setter: setShowCarbonTutorial,
        conditions: [project?.category === "CARBON_SINK"],
      },
    };

    let anyTutorialShown = false;
    Object.entries(tutorials).forEach(([key, { setter, conditions }]) => {
      const hasWatched = localStorage.getItem(key) === "true";
      const meetsConditions = conditions ? conditions.every(Boolean) : true;

      if (!hasWatched && meetsConditions) {
        setter(true);
        anyTutorialShown = true;
      }
    });

    if (anyTutorialShown) {
      setShowTutorial(true);
      setCurrentStep(0);
      if (openDrawer) setOpenDrawer(false);
    }
  }, [project, repeatTutorial]);

  const steps = [
    [
      {
        type: "main",
        title: t("tutorial_step_title_1"),
        body: t("tutorial_step_body_1"),
        onNext: handleNextStep,
      },
    ],
    ...(showBasicTutorial
      ? [
          [
            {
              type: "main",
              body: t("tutorial_step_body_2"),
              onNext: () => {
                if (isMobile) handleNextStep();
                else {
                  setOpenDrawer(true);
                  setChangingStep(true);
                  setTimeout(handleNextStep, 500);
                }
              },
              onPrevious: handlePreviousStep,
            },
            {
              type: "element",
              anchorEl: menuRef.current,
              body: t("tutorial_sidebar_button"),
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              transformOrigin: { vertical: "top", horizontal: "left" },
            },
            {
              type: "element",
              anchorEl: mapControlsRef.current,
              body: t("tutorial_map_controls_button"),
              anchorOrigin: { vertical: "top", horizontal: "right" },
              transformOrigin: { vertical: "bottom", horizontal: "right" },
            },
            ...(!isMobile
              ? [
                  {
                    type: "element",
                    anchorEl: userDirectionRef.current,
                    body: t("tutorial_user_directions_button"),
                    anchorOrigin: { vertical: "bottom", horizontal: "right" },
                    transformOrigin: { vertical: "top", horizontal: "right" },
                  },
                  {
                    type: "element",
                    anchorEl: attributionsRef.current,
                    body: t("tutorial_attributions_button"),
                    anchorOrigin: { vertical: "top", horizontal: "left" },
                    transformOrigin: { vertical: "bottom", horizontal: "left" },
                  },
                ]
              : []),
          ],
          ...(isMobile
            ? [
                [
                  {
                    type: "main",
                    body: t("tutorial_step_body_2"),
                    onNext: () => {
                      setOpenDrawer(true);
                      setChangingStep(true);
                      setTimeout(handleNextStep, 500);
                    },
                    onPrevious: handlePreviousStep,
                  },
                  {
                    type: "element",
                    anchorEl: userDirectionRef.current,
                    body: t("tutorial_user_directions_button"),
                    anchorOrigin: { vertical: "bottom", horizontal: "right" },
                    transformOrigin: { vertical: "top", horizontal: "right" },
                  },
                  {
                    type: "element",
                    anchorEl: attributionsRef.current,
                    body: t("tutorial_attributions_button"),
                    anchorOrigin: { vertical: "top", horizontal: "left" },
                    transformOrigin: { vertical: "bottom", horizontal: "left" },
                  },
                ],
              ]
            : []),
          [
            {
              type: "main",
              body: t("tutorial_step_body_3"),
              onNext: () => {
                setOpenDrawer(false);
                localStorage.setItem("watched_basic_tutorial", "true");

                if (!showCarbonTutorial && avatarRef.current) {
                  setAnchorElAvatar(avatarRef.current);
                  setChangingStep(true);
                  setTimeout(handleNextStep, 500);
                } else handleNextStep();
              },
              onPrevious: () => {
                setOpenDrawer(false);
                handlePreviousStep();
              },
              align: isMobile ? "bottom" : "right",
            },
            {
              type: "element",
              anchorEl: myProjectsRef.current,
              body: t("tutorial_my_projects_button"),
              anchorOrigin: isMobile
                ? { vertical: "bottom", horizontal: "center" }
                : { vertical: "center", horizontal: "right" },
              transformOrigin: isMobile
                ? { vertical: "top", horizontal: "center" }
                : { vertical: "center", horizontal: "left" },
            },
            {
              type: "element",
              anchorEl: tabsRef.current,
              body: t("tutorial_tabs"),
              anchorOrigin: isMobile
                ? { vertical: "top", horizontal: "center" }
                : { vertical: "center", horizontal: "right" },
              transformOrigin: isMobile
                ? { vertical: "bottom", horizontal: "center" }
                : { vertical: "center", horizontal: "left" },
            },
            {
              type: "element",
              anchorEl: layersRef.current,
              body: t("tutorial_layers"),
              anchorOrigin: isMobile
                ? { vertical: "top", horizontal: "center" }
                : { vertical: "center", horizontal: "right" },
              transformOrigin: isMobile
                ? { vertical: "top", horizontal: "center" }
                : { vertical: "center", horizontal: "left" },
            },
          ],
        ]
      : []),
    ...(showCarbonTutorial
      ? [
          [
            {
              type: "main",
              title: t("tutorial_carbon_title"),
              body: t("tutorial_carbon_body_1"),
              extra_content: (
                <img src="gifs/carbon_sink.gif" alt="Tutorial carbon sink" />
              ),
              onNext: () => {
                setCarbonResponse(mockedCarbonResponse);
                setActualGroundUse(
                  mockedCarbonResponse.ground_use_distribution
                );
                setShowTutorialCarbonWindow(true);
                setChangingStep(true);
                setTimeout(handleNextStep, 1000);
              },
              onPrevious: () => {
                if (showBasicTutorial) {
                  setOpenDrawer(true);
                  setChangingStep(true);
                  setTimeout(handlePreviousStep, 350);
                } else handlePreviousStep();
              },
            },
          ],
          [
            {
              type: "main",
              title: t("tutorial_carbon_title"),
              body: t("tutorial_carbon_body_2"),
              onNext: () => {
                localStorage.setItem("watched_carbon_tutorial", "true");
                setCarbonResponse({});
                setActualGroundUse({});
                setShowTutorialCarbonWindow(false);
                if (avatarRef.current) {
                  setAnchorElAvatar(avatarRef.current);
                }
                setChangingStep(true);
                setTimeout(handleNextStep, 500);
              },
              onPrevious: () => {
                setCarbonResponse({});
                setActualGroundUse({});
                setShowTutorialCarbonWindow(false);
                setChangingStep(true);
                setTimeout(handlePreviousStep, 350);
              },
              align: isMobile ? "bottom" : "left",
            },
            {
              type: "element",
              anchorEl: carbonCategoryRef.current,
              body: t("tutorial_carbon_category"),
              anchorOrigin: isMobile
                ? { vertical: "top", horizontal: "center" }
                : { vertical: "center", horizontal: "left" },
              transformOrigin: isMobile
                ? { vertical: "bottom", horizontal: "center" }
                : { vertical: "center", horizontal: "right" },
            },
          ],
        ]
      : []),
    [
      {
        type: "main",
        title: t("tutorial_step_title_4"),
        body: t("tutorial_step_body_4"),
        onPrevious: () => {
          setAnchorElAvatar(null);
          if (showCarbonTutorial) {
            setCarbonResponse(mockedCarbonResponse);
            setActualGroundUse(mockedCarbonResponse.ground_use_distribution);
            setShowTutorialCarbonWindow(true);
            setChangingStep(true);
            setTimeout(handlePreviousStep, 1000);
          } else if (showBasicTutorial) {
            setOpenDrawer(true);
            setChangingStep(true);
            setTimeout(handlePreviousStep, 350);
          } else handlePreviousStep();
        },
        onNext: handleFinishTutorial,
      },
      {
        type: "element",
        anchorEl: avatarRef.current,
      },
      {
        type: "element",
        anchorEl: avatarTutorialButtonRef.current,
        body: t("tutorial_repeat"),
        anchorOrigin: isMobile
          ? { vertical: "bottom", horizontal: "right" }
          : { vertical: "center", horizontal: "left" },
        transformOrigin: isMobile
          ? { vertical: "top", horizontal: "right" }
          : { vertical: "center", horizontal: "right" },
      },
    ],
  ];

  if (steps.length === 2 || !showTutorial) return;

  return (
    <Box>
      {steps[currentStep]?.map((stepItem, index) => {
        if (changingStep) return;

        if (stepItem.type === "main") {
          return (
            <StepBox
              key={`main-${currentStep}`}
              // step={currentStep}
              currentStep={currentStep}
              title={stepItem.title}
              body={stepItem.body}
              onNext={stepItem.onNext}
              onPrevious={stepItem.onPrevious}
              onSkip={handleSkip}
              align={stepItem.align}
              totalSteps={Object.keys(steps).length}
              extra_content={stepItem.extra_content}
            />
          );
        }

        if (stepItem.type === "element") {
          return (
            <GuidedPopover
              key={`element-${currentStep}-${index}`}
              anchorEl={stepItem.anchorEl}
              body={stepItem.body}
              anchorOrigin={stepItem.anchorOrigin}
              transformOrigin={stepItem.transformOrigin}
            />
          );
        }

        return null;
      })}
    </Box>
  );
}

const mockedCarbonResponse = {
  area_km2: 7.897288506668014,
  carbon_Mg: 204643.28923719545,
  carbon_Mg_per_km2: 25913.107905885227,
  carbon_credits_tCO2e: 751040.8715005073,
  carbon_credits_tCO2e_per_km2: 95101.10601459879,
  ground_use_distribution: {
    "%": {
      "Cobertura arb\u00f3rea": 0.3854,
      Matorral: 0.5033,
      "Tierras de cultivo": 0.0227,
      "Vegetaci\u00f3n escasa o desnuda": 0.0021,
      "\u00c1rea urbanizada": 0.0866,
    },
    ha: {
      "Cobertura arb\u00f3rea": 304.34,
      Matorral: 397.47,
      "Tierras de cultivo": 17.93,
      "Vegetaci\u00f3n escasa o desnuda": 1.62,
      "\u00c1rea urbanizada": 68.37,
    },
    km2: {
      "Cobertura arb\u00f3rea": 3.0434,
      Matorral: 3.9747,
      "Tierras de cultivo": 0.1793,
      "Vegetaci\u00f3n escasa o desnuda": 0.0162,
      "\u00c1rea urbanizada": 0.6837,
    },
    m2: {
      "Cobertura arb\u00f3rea": 3043395.18,
      Matorral: 3974682.95,
      "Tierras de cultivo": 179308.54,
      "Vegetaci\u00f3n escasa o desnuda": 16190.04,
      "\u00c1rea urbanizada": 683711.79,
    },
  },
  market_value_euros: 52272444.6564353,
  market_value_euros_per_km2: 6619036.978616075,
};
